import React, { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { getChartData } from '../../FeatureRedux/chartReducer/donatchartget';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from '../axiosInstance';
import { useNavigate } from 'react-router-dom';

// Register Chart.js elements
ChartJS.register(ArcElement, Tooltip, Legend, Title);

const ChartComponent = () => {
  const dispatch = useDispatch();
  const [selectedCountry, setSelectedCountry] = useState('India');
  const [activeChart, setActiveChart] = useState('states');
  const [selectedSector, setSelectedSector] = useState(null);
  const [sectorProjects, setSectorProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const navigate = useNavigate();

  // Redux state
  const { data, loading, error, isSuccess } = useSelector((state) => state.getChartData);

  useEffect(() => {
    dispatch(getChartData());
  }, [dispatch]);

  // Color palettes
  const sectorColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'];
  const monetaryColors = ['#FFCE56', '#36A2EB', '#FF6384', '#4BC0C0', '#9966FF'];
  const countryColors = ['#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0'];
  const stateColors = [
    '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
    '#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0',
    '#3DDC97', '#F7B32B', '#D64550', '#5C2751', '#6457A6',
    '#9BC53D', '#E55934', '#FA7921', '#3E5C76', '#1D1E2C'
  ];

  // Safely get data with fallbacks
  const sectors = data?.data?.sectors || {};
  const monetaryRange = data?.data?.MonetaryRange || {};
  const countriesData = data?.data?.countryData || {};

  // Prepare chart data for each graph
  const sectorsData = {
    labels: Object.keys(sectors).map((sector) => `${sector}: ${sectors[sector]}`),
    datasets: [{
      data: Object.values(sectors),
      backgroundColor: sectorColors,
      hoverBackgroundColor: sectorColors,
    }],
  };

  const handledetail = (project) => {
    navigate('/detail', { state: { project } });
  };

  const moneyData = {
    labels: Object.keys(monetaryRange),
    datasets: [{
      data: Object.values(monetaryRange),
      backgroundColor: monetaryColors,
      hoverBackgroundColor: monetaryColors,
    }],
  };

  const countriesChartData = {
    labels: Object.keys(countriesData),
    datasets: [{
      data: Object.values(countriesData).map(item => item.count),
      backgroundColor: countryColors,
      hoverBackgroundColor: countryColors,
    }],
  };

  const getStatesChartData = () => {
    if (!selectedCountry || !countriesData[selectedCountry]?.states) return null;
  
    const states = countriesData[selectedCountry].states;
    const isArray = Array.isArray(states);
  
    const statesLabels = isArray
      ? states.map((state) => state.substring(0, 2)) // Truncate to first 2 letters
      : Object.keys(states).map((state) => state.substring(0, 2)); // Truncate keys
  
    const statesValues = isArray
      ? new Array(states.length).fill(1)
      : Object.values(states);
  
    const colorsForStates = statesLabels.map((_, index) =>
      stateColors[index % stateColors.length]
    );
  
    return {
      labels: statesLabels,
      datasets: [{
        data: statesValues,
        backgroundColor: colorsForStates,
        hoverBackgroundColor: colorsForStates,
      }],
    };
  };

  const handleCountrySelected = (e) => {
    const country = e.target.value;
    setSelectedCountry(country);
    setActiveChart(country ? 'states' : 'countries');
  };

  const fetchProjectsBySector = async (sector) => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const response = await axiosInstance.get('/project/allProjects', {
        headers: {
          'x-company-id': '67ce66294f28f85e4ff91e2c'
        }
      });
      
      // Filter projects by the selected sector
      const filteredProjects = response.data.projects.filter(project => 
        project.sector === sector
      );
      
      setSectorProjects(filteredProjects);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjectsError('Failed to load projects. Please try again.');
      setSectorProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleSectorClick = (elements, chartId) => {
    // Only process clicks if they're on the sector chart
    if (chartId !== 'sector-chart' || elements.length === 0) return;
    
    const index = elements[0].index;
    const sector = Object.keys(sectors)[index];
    setSelectedSector(sector);
    fetchProjectsBySector(sector);
  };

  const getChartOptions = (chartId) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: 'white',
          padding: 20,
          boxWidth: 12
        }
      },
      title: {
        display: true,
        padding: { top: 10, bottom: 10 },
        font: { size: 16 },
        color: 'white'
      },
      tooltip: {
        mode: 'index',
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.raw ?? '';
            return `${label}`;
          },
          title: function () {
            return '';
          }
        }
      }
    },
    onClick: (event, elements) => {
      handleSectorClick(elements, chartId);
    }
  });

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  return (
    <div className="p-4 bg-gray-900"
      style={{
        overflowY: 'scroll',
        height: '100vh',
        width: '100%',
        scrollbarWidth: '1px',
        msOverflowStyle: 'none'
      }}>
      {/* Sector and Monetary Charts */}
      <div className="flex flex-wrap gap-8 justify-center mb-8">
        {/* Sectors Chart */}
        <div className="w-96 h-96 bg-gray-900 text-white border shadow p-4 rounded-lg" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="0">
          <h2 className="text-center mb-2 text-lg font-semibold">Sectors Data</h2>
          <div className="h-60">
            {loading ? (
              <p className="text-center pt-8">Loading...</p>
            ) : error ? (
              <p className="text-center pt-8 text-red-500">Error: {error}</p>
            ) : isSuccess && Object.keys(sectors).length > 0 ? (
              <Doughnut
                data={sectorsData}
                options={{
                  ...getChartOptions('sector-chart'),
                  plugins: {
                    ...getChartOptions('sector-chart').plugins,
                    title: { ...getChartOptions('sector-chart').plugins.title, text: 'Sectors Distribution' }
                  }
                }}
              />
            ) : (
              <p className="text-center pt-8 text-light">No Sector Data Available</p>
            )}
          </div>
        </div>

        {/* Monetary Ranges Chart */}
        <div className="w-96 h-96 bg-gray-900 text-white border border-white shadow p-4 rounded-lg" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="1000">
          <h2 className="text-center mb-2 text-lg font-semibold">Monetary Ranges</h2>
          <div className="h-60">
            {loading ? (
              <p className="text-center pt-8">Loading...</p>
            ) : error ? (
              <p className="text-center pt-8 text-red-500">Error: {error}</p>
            ) : isSuccess && Object.keys(monetaryRange).length > 0 ? (
              <Doughnut
                data={moneyData}
                options={{
                  ...getChartOptions('monetary-chart'),
                  plugins: {
                    ...getChartOptions('monetary-chart').plugins,
                    title: { ...getChartOptions('monetary-chart').plugins.title, text: 'Monetary Ranges' }
                  }
                }}
              />
            ) : (
              <p className="text-center pt-8 text-white">No Monetary Data Available</p>
            )}
          </div>
        </div>
        <div className="flex justify-center">
          <div className="w-full max-w-[500px] min-w-[300px] h-96 bg-gray-900 text-white border border-white shadow p-4 rounded-lg"
            data-aos="fade-up" data-aos-duration="1000" data-aos-delay="2000">
            <div className="mb-4">
              <label className="block text-sm font-medium text-center mb-2">Choose Country</label>
              <div className="relative">
                <select
                  className="block w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm appearance-none truncate"
                  onChange={handleCountrySelected}
                  value={selectedCountry}
                >
                  <option value="">Show All Countries</option>
                  {isSuccess && Object.keys(countriesData).map((country, index) => (
                    <option key={index} value={country} className="truncate">
                      {country.length > 20 ? `${country.substring(0, 20)}...` : country}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="h-64">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-center">Loading...</p>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-center text-red-500 px-2">Error: {error}</p>
                </div>
              ) : isSuccess ? (
                activeChart === 'countries' ? (
                  Object.keys(countriesData).length > 0 ? (
                    <Doughnut
                      data={countriesChartData}
                      options={{
                        ...getChartOptions('country-chart'),
                        plugins: {
                          ...getChartOptions('country-chart').plugins,
                          title: {
                            ...getChartOptions('country-chart').plugins.title,
                            text: 'Countries Distribution',
                            font: {
                              size: 14
                            },
                            padding: {
                              top: 10,
                              bottom: 10
                            }
                          }
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full px-2">
                      <p className="text-center text-white">No Country Data Available</p>
                    </div>
                  )
                ) : getStatesChartData() ? (
                  <Doughnut
                    data={getStatesChartData()}
                    options={{
                      ...getChartOptions('state-chart'),
                      plugins: {
                        ...getChartOptions('state-chart').plugins,
                        title: {
                          ...getChartOptions('state-chart').plugins.title,
                          text: `${selectedCountry.length > 15 ? `${selectedCountry.substring(0, 15)}...` : selectedCountry} States`,
                          font: {
                            size: 14
                          },
                          padding: {
                            top: 10,
                            bottom: 10
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full px-2">
                    <p className="text-center text-white">No State Data Available for {selectedCountry.length > 15 ? `${selectedCountry.substring(0, 15)}...` : selectedCountry}</p>
                  </div>
                )
              ) : (
                <div className="flex items-center justify-center h-full px-2">
                  <p className="text-center text-white">No Data Available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Projects Section */}
      {selectedSector && (
        <div className="mt-8" data-aos="fade-up" data-aos-duration="1000">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Projects in {selectedSector} Sector
            {/* {projectsLoading && <span className="ml-2 text-sm text-gray-400">(Loading...)</span>} */}
          </h2>
          
          {projectsError ? (
            <div className="text-center text-red-500 mb-6">{projectsError}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
  {sectorProjects.length > 0 ? (
    sectorProjects.map((project) => (
      <div 
        key={project._id} 
        className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden hover:border-blue-500 transition-colors duration-200"
      >
        <div className="p-5">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <h3 
              onClick={() => handledetail(project)} 
              className="text-lg font-semibold text-white hover:text-blue-400 cursor-pointer truncate max-w-[180px]"
              title={project.project_name || 'Unnamed Project'}
            >
              {project.project_name ? 
                (project.project_name.length > 20 ? 
                  `${project.project_name.substring(0, 20)}...` : 
                  project.project_name) : 
                'Unnamed Project'}
            </h3>
            {project.status && (
              <span className={`px-2.5 py-1 text-xs rounded-full ${
                project.status === 'Completed' ? 'bg-green-500/20 text-green-400' :
                project.status === 'In Progress' ? 'bg-blue-500/20 text-blue-400' :
                project.status === 'Planning' ? 'bg-purple-500/20 text-purple-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {project.status}
              </span>
            )}
          </div>

          {/* Rest of the card content remains exactly the same */}
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {project.sector && (
              <span className="px-2.5 py-1 bg-gray-700/40 text-gray-200 rounded-full text-xs">
                <span className="opacity-70">🏗️</span> {project.sector}
              </span>
            )}
            {project.country && (
              <span className="px-2.5 py-1 bg-gray-700/40 text-gray-200 rounded-full text-xs">
                <span className="opacity-70">🌎</span> {project.country}
              </span>
            )}
            {project.state && (
              <span className="px-2.5 py-1 bg-gray-700/40 text-gray-200 rounded-full text-xs">
                <span className="opacity-70">📍</span> {project.state}
              </span>
            )}
          </div>

          {/* Details */}
          <div className="space-y-3 mb-5">
            {project.area && (
              <div className="flex items-center gap-3 text-gray-300">
                <div className="text-gray-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Area: </span>
                  {project.area} {project.areaUnit || ''}
                </div>
              </div>
            )}

            {project.cost && (
              <div className="flex items-center gap-3 text-gray-300">
                <div className="text-gray-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Budget: </span>
                  {formatCurrency(project.cost)}
                </div>
              </div>
            )}

            {project.startDate && (
              <div className="flex items-center gap-3 text-gray-300">
                <div className="text-gray-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Start: </span>
                  {formatDate(project.startDate)}
                </div>
              </div>
            )}

            {project.endDate && (
              <div className="flex items-center gap-3 text-gray-300">
                <div className="text-gray-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">End: </span>
                  {formatDate(project.endDate)}
                </div>
              </div>
            )}
          </div>

          {/* Action */}
          <button 
            onClick={() => handledetail(project)} 
            className="w-full py-2 px-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm flex items-center justify-between transition-colors"
          >
            View details
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    ))
  ) : (
    !projectsLoading && (
      <div className="col-span-full text-center py-12">
        <div className="mx-auto w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mb-4">
          <svg className="w-10 h-10 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h4 className="text-gray-300 text-lg mb-1">No projects found</h4>
        <p className="text-gray-500 text-sm">Try adjusting your search criteria</p>
      </div>
    )
  )}
</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChartComponent;


// import React, { useEffect, useState } from 'react';
// import { Doughnut } from 'react-chartjs-2';
// import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
// import { getChartData } from '../../FeatureRedux/chartReducer/donatchartget';
// import { useDispatch, useSelector } from 'react-redux';
// import axiosInstance from '../axiosInstance';
// import { useNavigate } from 'react-router-dom';

// // Register Chart.js elements
// ChartJS.register(ArcElement, Tooltip, Legend, Title);

// const ChartComponent = () => {
//   const dispatch = useDispatch();
//   const [selectedCountry, setSelectedCountry] = useState('India');
//   const [activeChart, setActiveChart] = useState('states');
//   const [selectedSector, setSelectedSector] = useState(null);
//   const [sectorProjects, setSectorProjects] = useState([]);
//   const [projectsLoading, setProjectsLoading] = useState(false);
//   const [projectsError, setProjectsError] = useState(null);
//   const navigate = useNavigate();

//   // Redux state
//   const { data, loading, error, isSuccess } = useSelector((state) => state.getChartData);

//   useEffect(() => {
//     dispatch(getChartData());
//   }, [dispatch]);

//   // Color palettes
//   const sectorColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'];
//   const monetaryColors = ['#FFCE56', '#36A2EB', '#FF6384', '#4BC0C0', '#9966FF'];
//   const countryColors = ['#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0'];
//   const stateColors = [
//     '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
//     '#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0',
//     '#3DDC97', '#F7B32B', '#D64550', '#5C2751', '#6457A6',
//     '#9BC53D', '#E55934', '#FA7921', '#3E5C76', '#1D1E2C'
//   ];

//   // Safely get data with fallbacks
//   const sectors = data?.data?.sectors || {};
//   const monetaryRange = data?.data?.MonetaryRange || {};
//   const countriesData = data?.data?.countryData || {};

//   // Prepare chart data for each graph
//   const sectorsData = {
//     labels: Object.keys(sectors).map((sector) => `${sector}: ${sectors[sector]}`),
//     datasets: [{
//       data: Object.values(sectors),
//       backgroundColor: sectorColors,
//       hoverBackgroundColor: sectorColors,
//     }],
//   };

//   const handledetail = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   const moneyData = {
//     labels: Object.keys(monetaryRange),
//     datasets: [{
//       data: Object.values(monetaryRange),
//       backgroundColor: monetaryColors,
//       hoverBackgroundColor: monetaryColors,
//     }],
//   };

//   const countriesChartData = {
//     labels: Object.keys(countriesData),
//     datasets: [{
//       data: Object.values(countriesData).map(item => item.count),
//       backgroundColor: countryColors,
//       hoverBackgroundColor: countryColors,
//     }],
//   };

//   const getStatesChartData = () => {
//     if (!selectedCountry || !countriesData[selectedCountry]?.states) return null;
  
//     const states = countriesData[selectedCountry].states;
//     const isArray = Array.isArray(states);
  
//     const statesLabels = isArray
//       ? states.map((state) => state.substring(0, 2)) // Truncate to first 2 letters
//       : Object.keys(states).map((state) => state.substring(0, 2)); // Truncate keys
  
//     const statesValues = isArray
//       ? new Array(states.length).fill(1)
//       : Object.values(states);
  
//     const colorsForStates = statesLabels.map((_, index) =>
//       stateColors[index % stateColors.length]
//     );
  
//     return {
//       labels: statesLabels,
//       datasets: [{
//         data: statesValues,
//         backgroundColor: colorsForStates,
//         hoverBackgroundColor: colorsForStates,
//       }],
//     };
//   };

//   const handleCountrySelected = (e) => {
//     const country = e.target.value;
//     setSelectedCountry(country);
//     setActiveChart(country ? 'states' : 'countries');
//   };

//   const fetchProjectsBySector = async (sector) => {
//     setProjectsLoading(true);
//     setProjectsError(null);
//     try {
//       const response = await axiosInstance.get('/project/allProjects', {
//         headers: {
//           'x-company-id': '67ce66294f28f85e4ff91e2c'
//         }
//       });
      
//       // Filter projects by the selected sector
//       const filteredProjects = response.data.projects.filter(project => 
//         project.sector === sector
//       );
      
//       setSectorProjects(filteredProjects);
//     } catch (err) {
//       console.error('Error fetching projects:', err);
//       setProjectsError('Failed to load projects. Please try again.');
//       setSectorProjects([]);
//     } finally {
//       setProjectsLoading(false);
//     }
//   };

//   const handleSectorClick = (elements) => {
//     if (elements.length > 0) {
//       const index = elements[0].index;
//       const sector = Object.keys(sectors)[index];
//       setSelectedSector(sector);
//       fetchProjectsBySector(sector);
//     }
//   };

//   const chartOptions = {
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: {
//         position: 'bottom',
//         labels: {
//           color: 'white',
//           padding: 20,
//           boxWidth: 12
//         }
//       },
//       title: {
//         display: true,
//         padding: { top: 10, bottom: 10 },
//         font: { size: 16 },
//         color: 'white'
//       },
//       tooltip: {
//         mode: 'index', // ✅ Show only one tooltip line
//         callbacks: {
//           label: function (context) {
//             const label = context.label || '';
//             const value = context.raw ?? '';
//             return `${label}`; // ✅ Combine into one line
//           },
//           title: function () {
//             return ''; // ✅ Remove extra title line
//           }
//         }
//       }
//     },
//     onClick: (event, elements) => {
//       handleSectorClick(elements);
//     }
//   };
  

//   const formatCurrency = (value) => {
//     if (!value) return '-';
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 0,
//       maximumFractionDigits: 0
//     }).format(value);
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return '-';
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-GB');
//   };

//   return (
//     <div className="p-4 bg-gray-900"
//       style={{
//         overflowY: 'scroll',
//         height: '100vh',
//         width: '100%',
//         scrollbarWidth: '1px',
//         msOverflowStyle: 'none'
//       }}>
//       {/* Sector and Monetary Charts */}
//       <div className="flex flex-wrap gap-8 justify-center mb-8">
//         {/* Sectors Chart */}
//         <div className="w-96 h-96 bg-gray-900 text-white border shadow p-4 rounded-lg" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="0">
//           <h2 className="text-center mb-2 text-lg font-semibold">Sectors Data</h2>
//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(sectors).length > 0 ? (
//               <Doughnut
//                 data={sectorsData}
//                 options={{
//                   ...chartOptions,
//                   plugins: {
//                     ...chartOptions.plugins,
//                     title: { ...chartOptions.plugins.title, text: 'Sectors Distribution' }
//                   }
//                 }}
//               />
//             ) : (
//               <p className="text-center pt-8 text-light">No Sector Data Available</p>
//             )}
//           </div>
//         </div>

//         {/* Monetary Ranges Chart */}
//         <div className="w-96 h-96 bg-gray-900 text-white border border-white shadow p-4 rounded-lg" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="1000">
//           <h2 className="text-center mb-2 text-lg font-semibold">Monetary Ranges</h2>
//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(monetaryRange).length > 0 ? (
//               <Doughnut
//                 data={moneyData}
//                 options={{
//                   ...chartOptions,
//                   plugins: {
//                     ...chartOptions.plugins,
//                     title: { ...chartOptions.plugins.title, text: 'Monetary Ranges' }
//                   }
//                 }}
//               />
//             ) : (
//               <p className="text-center pt-8 text-white">No Monetary Data Available</p>
//             )}
//           </div>
//         </div>
//         <div className="flex justify-center">
//           <div className="w-full max-w-[500px] min-w-[300px] h-96 bg-gray-900 text-white border border-white shadow p-4 rounded-lg"
//             data-aos="fade-up" data-aos-duration="1000" data-aos-delay="2000">
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-center mb-2">Choose Country</label>
//               <div className="relative">
//                 <select
//                   className="block w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm appearance-none truncate"
//                   onChange={handleCountrySelected}
//                   value={selectedCountry}
//                 >
//                   <option value="">Show All Countries</option>
//                   {isSuccess && Object.keys(countriesData).map((country, index) => (
//                     <option key={index} value={country} className="truncate">
//                       {country.length > 20 ? `${country.substring(0, 20)}...` : country}
//                     </option>
//                   ))}
//                 </select>
//                 <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
//                   <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
//                     <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
//                   </svg>
//                 </div>
//               </div>
//             </div>

//             <div className="h-64">
//               {loading ? (
//                 <div className="flex items-center justify-center h-full">
//                   <p className="text-center">Loading...</p>
//                 </div>
//               ) : error ? (
//                 <div className="flex items-center justify-center h-full">
//                   <p className="text-center text-red-500 px-2">Error: {error}</p>
//                 </div>
//               ) : isSuccess ? (
//                 activeChart === 'countries' ? (
//                   Object.keys(countriesData).length > 0 ? (
//                     <Doughnut
//                       data={countriesChartData}
//                       options={{
//                         ...chartOptions,
//                         plugins: {
//                           ...chartOptions.plugins,
//                           title: {
//                             ...chartOptions.plugins.title,
//                             text: 'Countries Distribution',
//                             font: {
//                               size: 14
//                             },
//                             padding: {
//                               top: 10,
//                               bottom: 10
//                             }
//                           }
//                         }
//                       }}
//                     />
//                   ) : (
//                     <div className="flex items-center justify-center h-full px-2">
//                       <p className="text-center text-white">No Country Data Available</p>
//                     </div>
//                   )
//                 ) : getStatesChartData() ? (
//                   <Doughnut
//                     data={getStatesChartData()}
//                     options={{
//                       ...chartOptions,
//                       plugins: {
//                         ...chartOptions.plugins,
//                         title: {
//                           ...chartOptions.plugins.title,
//                           text: `${selectedCountry.length > 15 ? `${selectedCountry.substring(0, 15)}...` : selectedCountry} States`,
//                           font: {
//                             size: 14
//                           },
//                           padding: {
//                             top: 10,
//                             bottom: 10
//                           }
//                         }
//                       }
//                     }}
//                   />
//                 ) : (
//                   <div className="flex items-center justify-center h-full px-2">
//                     <p className="text-center text-white">No State Data Available for {selectedCountry.length > 15 ? `${selectedCountry.substring(0, 15)}...` : selectedCountry}</p>
//                   </div>
//                 )
//               ) : (
//                 <div className="flex items-center justify-center h-full px-2">
//                   <p className="text-center text-white">No Data Available</p>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Projects Section */}
//       {selectedSector && (
//         <div className="mt-8" data-aos="fade-up" data-aos-duration="1000">
//           <h2 className="text-2xl font-bold text-white mb-6 text-center">
//             Projects in {selectedSector} Sector
//             {projectsLoading && <span className="ml-2 text-sm text-gray-400">(Loading...)</span>}
//           </h2>
          
//           {projectsError ? (
//             <div className="text-center text-red-500 mb-6">{projectsError}</div>
//           ) : (
//             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//               {sectorProjects.length > 0 ? (
//                 sectorProjects.map((project) => (
//                   <div key={project._id} className="bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-gray-700">
//                     <div className="p-6">
//                       <h3 onClick={() => handledetail(project)} className="text-xl font-semibold text-white mb-2 cursor-pointer">{project.project_name || 'Unnamed Project'}</h3>
//                       <div className="flex flex-wrap gap-2 mb-4">
//                         {project.sector && (
//                           <span className="px-2 py-1 bg-blue-900 text-blue-300 rounded text-xs">
//                             {project.sector}
//                           </span>
//                         )}
//                         {project.country && (
//                           <span className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs">
//                             {project.country}
//                           </span>
//                         )}
//                         {project.state && (
//                           <span className="px-2 py-1 bg-purple-900 text-purple-300 rounded text-xs">
//                             {project.state}
//                           </span>
//                         )}
//                       </div>
//                       <div className="space-y-2 text-sm text-gray-300 mb-4">
//                         {project.area && (
//                           <p><span className="font-medium">Area:</span> {project.area} {project.areaUnit || ''}</p>
//                         )}
//                         {project.cost && (
//                           <p><span className="font-medium">Budget:</span> {formatCurrency(project.cost)}</p>
//                         )}
//                         {project.startDate && (
//                           <p><span className="font-medium">Start:</span> {formatDate(project.startDate)}</p>
//                         )}
//                         {project.endDate && (
//                           <p><span className="font-medium">End:</span> {formatDate(project.endDate)}</p>
//                         )}
//                       </div>
//                       <div className="flex justify-between items-center">
//                         {project.status && (
//                           <span className={`px-3 py-1 rounded-full text-sm font-medium ${
//                             project.status === 'Completed' ? 'bg-green-900 text-green-300' :
//                             project.status === 'In Progress' ? 'bg-blue-900 text-blue-300' :
//                             project.status === 'Planning' ? 'bg-purple-900 text-purple-300' :
//                             'bg-gray-700 text-gray-300'
//                           }`}>
//                             {project.status}
//                           </span>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 ))
//               ) : (
//                 !projectsLoading && (
//                   <div className="col-span-full text-center py-8">
//                     <p className="text-gray-400">No projects found for this sector</p>
//                   </div>
//                 )
//               )}
//             </div>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// export default ChartComponent;

// import React, { useEffect, useState } from 'react';
// import { Doughnut } from 'react-chartjs-2';
// import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
// import { getChartData } from '../../FeatureRedux/chartReducer/donatchartget';
// import { useDispatch, useSelector } from 'react-redux';

// // Register Chart.js elements
// ChartJS.register(ArcElement, Tooltip, Legend, Title);

// const ChartComponent = () => {
//   const dispatch = useDispatch();
//   const [selectedCountry, setSelectedCountry] = useState('');
//   const [countryData, setCountryData] = useState([]);
//   const [stateData, setStateData] = useState(null);

//   // Redux state
//   const { data, loading, error, isSuccess } = useSelector((state) => state.getChartData); 

//   useEffect(() => { 
//     if (data?.data?.countryData) {
//       const formattedData = Object.entries(data.data.countryData).map(([country, countryInfo]) => ({
//         country,
//         states: countryInfo.states
//       }));
//       setCountryData(formattedData);
//     }
//   }, [data]);

//   useEffect(() => {
//     dispatch(getChartData());
//   }, [dispatch]);

//   // Handle API data safely
//   const sectors = data?.data?.sectors || {};
//   const monetaryRange = data?.data?.MonetaryRange || {};
//   const countriesData = data?.data?.countryData || {};

//   // Prepare chart data
//   const sectorsData = {
//     labels: Object.keys(sectors),
//     datasets: [
//       {
//         data: Object.values(sectors),
//         backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
//         hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
//       },
//     ],
//   };

//   const moneyData = {
//     labels: Object.keys(monetaryRange),
//     datasets: [
//       {
//         data: Object.values(monetaryRange),
//         backgroundColor: ['#FFCE56', '#36A2EB', '#FF6384', '#4BC0C0', '#9966FF'],
//         hoverBackgroundColor: ['#FFCE56', '#36A2EB', '#FF6384', '#4BC0C0', '#9966FF'],
//       },
//     ],
//   };

//   const countriesChartData = {
//     labels: Object.keys(countriesData),
//     datasets: [
//       {
//         data: Object.values(countriesData).map(country => country.count),
//         backgroundColor: ['#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0'],
//         hoverBackgroundColor: ['#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0'],
//       },
//     ],
//   };

//   function handleCountrySelected(e) {
//     const countrySelected = e.target.value;
//     setSelectedCountry(countrySelected);

//     // Find the selected country's data
//     const selectedCountryData = countryData.find(item => item.country === countrySelected);

//     if (selectedCountryData && selectedCountryData.states) {
//       // Prepare state data for the chart
//       const statesChartData = {
//         labels: Object.keys(selectedCountryData.states),
//         datasets: [{
//           data: Object.values(selectedCountryData.states),
//           backgroundColor: ['#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0'],
//           hoverBackgroundColor: ['#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0'],
//         }]
//       };
//       setStateData(statesChartData);
//     } else {
//       setStateData(null);
//     }
//   }

//   const chartOptions = {
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: { position: 'bottom' },
//       title: { display: true, text: 'Doughnut Chart' },
//     },
//   };

//   return (
//     <div className="p-4">
//       {/* Main Charts - Sector and Monetary */}
//       <div className="flex flex-wrap gap-8 justify-center mb-8">
//         {/* Sectors Pie Chart */}
//         <div className="w-96 h-80 bg-white shadow p-4 rounded-lg">
//           <h2 className="text-center mb-2 text-lg font-semibold">Sectors Data</h2>
//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(sectors).length > 0 ? (
//               <Doughnut data={sectorsData} options={chartOptions} />
//             ) : (
//               <p className="text-center pt-8">No Data Available</p>
//             )}
//           </div>
//         </div>

//         {/* Monetary Ranges Pie Chart */}
//         <div className="w-96 h-80 bg-white shadow p-4 rounded-lg">
//           <h2 className="text-center mb-2 text-lg font-semibold">Monetary Ranges</h2>
//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(monetaryRange).length > 0 ? (
//               <Doughnut data={moneyData} options={chartOptions} />
//             ) : (
//               <p className="text-center pt-8">No Data Available</p>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Country, State, City Charts */}
//       <div className="flex flex-wrap gap-8 justify-center">
//         {/* Countries Chart */}
//         <div className="w-96 h-80 bg-white shadow p-4 rounded-lg">
//           <h2 className="text-center mb-2 text-lg font-semibold">
//             {selectedCountry ? `${selectedCountry} States Data` : "Countries Data"}
//           </h2>

//           {/* Dropdown Selector for Options */}
//           <div className="mb-4">
//             <label htmlFor="optionSelector" className="block text-sm font-medium">Choose Country</label>
//             <select
//               id="optionSelector"
//               className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//               onChange={handleCountrySelected}
//               value={selectedCountry}
//             >
//               <option value="">Select a country</option>
//               {isSuccess && countryData.length > 0 && 
//                 countryData.map((country, index) => (
//                   <option key={index} value={country.country}>{country.country}</option>
//                 ))
//               }
//             </select>
//           </div>

//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : stateData ? (
//               <Doughnut data={stateData} options={chartOptions} />
//             ) : isSuccess && Object.keys(countriesData).length > 0 ? (
//               <Doughnut data={countriesChartData} options={chartOptions} />
//             ) : (
//               <p className="text-center pt-8">No Data Available</p>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ChartComponent;






// import React, { useEffect, useState } from 'react';
// import { Doughnut } from 'react-chartjs-2';
// import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
// import { getChartData } from '../../FeatureRedux/chartReducer/donatchartget';
// import { useDispatch, useSelector } from 'react-redux';
// import { Country, State, City } from 'country-state-city';

// // Register Chart.js elements
// ChartJS.register(ArcElement, Tooltip, Legend, Title);

// const ChartComponent = () => {
//   const dispatch = useDispatch();
//   const [selectedCountry, setSelectedCountry] = useState('');
//   const [country , setCountry] = useState()

//   // Redux state
//   const { data, loading, error, isSuccess } = useSelector((state) => state.getChartData); 

//   useEffect(() => { 
//     const countryData = data?.data?.countryData || {};

//     const formattedData = Object.keys(countryData).map(country => ({
//       country,
//       states: countryData[country].states
//     }));

//     setCountry(formattedData);

//     console.log(`Formatted Country States: ${JSON.stringify(formattedData, null, 2)}`);
//   }, [data]);



//   // console.log("this si counrty in state",country)

//   useEffect(() => {
//     dispatch(getChartData());
//   }, [dispatch]);

//   // Handle API data safely
//   const sectors = data?.data.sectors || {};
//   const monetaryRange = data?.data.MonetaryRange || {};
//   const countriesData = data?.data.countryData || {};

//   // Prepare chart data
//   const sectorsData = {
//     labels: Object.keys(sectors),
//     datasets: [
//       {
//         data: Object.values(sectors),
//         backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
//         hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
//       },
//     ],
//   };

//   const moneyData = {
//     labels: Object.keys(monetaryRange),
//     datasets: [
//       {
//         data: Object.values(monetaryRange),
//         backgroundColor: ['#FFCE56', '#36A2EB', '#FF6384', '#4BC0C0', '#9966FF'],
//         hoverBackgroundColor: ['#FFCE56', '#36A2EB', '#FF6384', '#4BC0C0', '#9966FF'],
//       },
//     ],
//   };

//   const countriesChartData = {
//     labels: Object.keys(countriesData),
//     datasets: [
//       {
//         data: Object.values(countriesData),
//         backgroundColor: ['#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0'],
//         hoverBackgroundColor: ['#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0'],
//       },
//     ],
//   };


//   const [stateshow , setStateshow] = useState()


//   // function handleCountrySelected (e){
//   //   // country
//   //   setSelectedCountry(e.target.value)
//   //   console.log(selectedCountry)
//   // }


//   function handleCountrySelected(e) {
//     const countrySelected = e.target.value;  // Get the selected country
//     setSelectedCountry(countrySelected);  // Set selected country

//     // Find the country in the countryData array
//     const countryObj = country.find(item => item.country === countrySelected);

//     if (countryObj) {
//       setStateshow(countryObj.states);  // Set the states of the selected country
//       console.log(`hisjagijaijdgija ${stateshow}`)
//     } else {
//       setStateshow([]);  // Clear states if country is not found
//     }
//   }



//   const chartOptions = {
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: { position: 'bottom' },
//       title: { display: true, text: 'Doughnut Chart' },
//     },
//   };


//   return (
//     <div className="p-4">
//       {/* Main Charts - Sector and Monetary */}
//       <div className="flex flex-wrap gap-8 justify-center mb-8">
//         {/* Sectors Pie Chart */}
//         <div className="w-96 h-80 bg-white shadow p-4 rounded-lg">
//           <h2 className="text-center mb-2 text-lg font-semibold">Sectors Data</h2>
//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(sectors).length > 0 ? (
//               <Doughnut data={sectorsData} options={chartOptions} />
//             ) : (
//               <p className="text-center pt-8">No Data Available</p>
//             )}
//           </div>
//         </div>

//         {/* Monetary Ranges Pie Chart */}
//         <div className="w-96 h-80 bg-white shadow p-4 rounded-lg">
//           <h2 className="text-center mb-2 text-lg font-semibold">Monetary Ranges</h2>
//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(monetaryRange).length > 0 ? (
//               <Doughnut data={moneyData} options={chartOptions} />
//             ) : (
//               <p className="text-center pt-8">No Data Available</p>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Location Selection - Row Style Dropdowns */}

//       {/* Country, State, City Charts */}
//       <div className="flex flex-wrap gap-8 justify-center">
//         {/* Countries Chart */}
//         <div className="w-96 h-80 bg-white shadow p-4 rounded-lg">
//           <h2 className="text-center mb-2 text-lg font-semibold">Countries Data</h2>

//           {/* Dropdown Selector for Options */}
//           <div className="mb-4">
//             <label htmlFor="optionSelector" className="block text-sm font-medium">Choose Option</label>
//             <select
//               id="optionSelector"
//               className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//               onChange={(e) => handleCountrySelected(e)} // Handle the change as needed
//             >
//               {isSuccess && Object.keys(countriesData).length > 0 ? 
//                 Object.keys(countriesData).map((country, index) => (
//                   <option key={index} value={country}>{country}</option>
//                 )
//               ) : (<option value="">Select Option</option>)}

//             </select>
//           </div>

//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(countriesData).length > 0 ? (
//               <Doughnut data={countriesChartData} options={chartOptions} />
//             ) : (
//               <p className="text-center pt-8">No Data Available</p>
//             )}
//           </div>
//         </div>


//       </div>
//     </div>
//   );
// };

// export default ChartComponent;


// import React, { useEffect, useState } from 'react';
// import { Doughnut } from 'react-chartjs-2';
// import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
// import { getChartData } from '../../FeatureRedux/chartReducer/donatchartget';
// import { useDispatch, useSelector } from 'react-redux';

// // Register Chart.js elements
// ChartJS.register(ArcElement, Tooltip, Legend, Title);

// const ChartComponent = () => {
//   const dispatch = useDispatch();
//   const [selectedCountry, setSelectedCountry] = useState('');
//   const [activeChart, setActiveChart] = useState('countries');

//   // Redux state
//   const { data, loading, error, isSuccess } = useSelector((state) => state.getChartData);

//   useEffect(() => {
//     dispatch(getChartData());
//   }, [dispatch]);

//   // Color palettes
//   const sectorColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'];
//   const monetaryColors = ['#FFCE56', '#36A2EB', '#FF6384', '#4BC0C0', '#9966FF'];
//   const countryColors = ['#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0'];
//   const stateColors = [
//     '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
//     '#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0',
//     '#3DDC97', '#F7B32B', '#D64550', '#5C2751', '#6457A6',
//     '#9BC53D', '#E55934', '#FA7921', '#3E5C76', '#1D1E2C'
//   ];

//   // Safely get data with fallbacks
//   const sectors = data?.data?.sectors || {};
//   const monetaryRange = data?.data?.MonetaryRange || {};
//   const countriesData = data?.data?.countryData || {};

//   // Prepare chart data for each graph
//   const sectorsData = {
//     labels: Object.keys(sectors),
//     datasets: [{
//       data: Object.values(sectors),
//       backgroundColor: sectorColors,
//       hoverBackgroundColor: sectorColors,
//     }],
//   };

//   const moneyData = {
//     labels: Object.keys(monetaryRange),
//     datasets: [{
//       data: Object.values(monetaryRange),
//       backgroundColor: monetaryColors,
//       hoverBackgroundColor: monetaryColors,
//     }],
//   };

//   const countriesChartData = {
//     labels: Object.keys(countriesData),
//     datasets: [{
//       data: Object.values(countriesData).map(item => item.count),
//       backgroundColor: countryColors,
//       hoverBackgroundColor: countryColors,
//     }],
//   };

//   const getStatesChartData = () => {
//     if (!selectedCountry || !countriesData[selectedCountry]?.states) return null;

//     const states = countriesData[selectedCountry].states;

//     // Handle both array and object formats
//     const statesLabels = Array.isArray(states) ? states : Object.keys(states);
//     const statesValues = Array.isArray(states) 
//       ? new Array(states.length).fill(1) // Equal values if array
//       : Object.values(states); // Actual values if object

//     // Assign a unique color to each state
//     const colorsForStates = statesLabels.map((_, index) => 
//       stateColors[index % stateColors.length]
//     );

//     return {
//       labels: statesLabels,
//       datasets: [{
//         data: statesValues,
//         backgroundColor: colorsForStates,
//         hoverBackgroundColor: colorsForStates,
//       }],
//     };
//   };

//   const handleCountrySelected = (e) => {
//     const country = e.target.value;
//     setSelectedCountry(country);
//     setActiveChart(country ? 'states' : 'countries');
//   };

//   const chartOptions = {
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: { 
//         position: 'bottom',
//         labels: {
//           padding: 20,
//           boxWidth: 12
//         }
//       },
//       title: { 
//         display: true,
//         padding: { top: 10, bottom: 10 },
//         font: { size: 16 }
//       },
//     },
//   };

//   return (
//     <div className="p-4 bg-gray-900"
//     style={{ 
//       overflowY: 'scroll',
//       height: '100vh',
//       width: '100%',
//       scrollbarWidth: '1px',
//       msOverflowStyle: 'none'
//   }}>
//       {/* Sector and Monetary Charts - Always Visible */}
//       <div className="flex flex-wrap gap-8 justify-center mb-8">
//         {/* Sectors Chart */}
//         <div className="w-96 h-80 bg-gray-900 text-white  border shadow p-4 rounded-lg" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="0">
//           <h2 className="text-center mb-2 text-lg font-semibold">Sectors Data</h2>
//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(sectors).length > 0 ? (
//               <Doughnut 
//                 data={sectorsData} 
//                 options={{
//                   ...chartOptions,
//                   plugins: {
//                     ...chartOptions.plugins,
//                     title: { ...chartOptions.plugins.title, text: 'Sectors Distribution', color: 'white' }
//                   }
//                 }} 
//               />
//             ) : (
//               <p className="text-center pt-8 text-light">No Sector Data Available</p>
//             )}
//           </div>
//         </div>

//         {/* Monetary Ranges Chart */}
//         <div className="w-96 h-80 bg-gray-900 text-white border shadow p-4 rounded-lg" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="1000">
//           <h2 className="text-center mb-2 text-lg font-semibold">Monetary Ranges</h2>
//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(monetaryRange).length > 0 ? (
//               <Doughnut 
//                 data={moneyData} 
//                 options={{
//                   ...chartOptions,
//                   plugins: {
//                     ...chartOptions.plugins,
//                     title: { ...chartOptions.plugins.title, text: 'Monetary Ranges', color: 'white' }
//                   }
//                 }} 
//               />
//             ) : (
//               <p className="text-center pt-8 text-white">No Monetary Data Available</p>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Combined Country/State Chart */}
//       <div className="flex justify-center">
//         <div className="w-96 h-80 bg-gray-900 text-white border shadow p-6 rounded-lg" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="2000">
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-center">Choose Country</label>
//             <select
//               className="mt-2 block w-full px-3 py-2 border bg-gray-900 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//               onChange={handleCountrySelected}
//               value={selectedCountry}
//             >
//               <option value="">Show All Countries</option>
//               {isSuccess && Object.keys(countriesData).map((country, index) => (
//                 <option key={index} value={country}>{country}</option>
//               ))}
//             </select>
//           </div>

//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess ? (
//               activeChart === 'countries' ? (
//                 Object.keys(countriesData).length > 0 ? (
//                   <Doughnut 
//                     data={countriesChartData} 
//                     options={{
//                       ...chartOptions,
//                       plugins: {
//                         ...chartOptions.plugins,
//                         title: { ...chartOptions.plugins.title, text: 'Countries Distribution' , color: 'white'}
//                       }
//                     }} 
//                   />
//                 ) : (
//                   <p className="text-center pt-8 text-white">No Country Data Available</p>
//                 )
//               ) : getStatesChartData() ? (
//                 <Doughnut 
//                   data={getStatesChartData()} 
//                   options={{
//                     ...chartOptions,
//                     plugins: {
//                       ...chartOptions.plugins,
//                       title: { ...chartOptions.plugins.title, text: `${selectedCountry} States` }
//                     }
//                   }} 
//                 />
//               ) : (
//                 <p className="text-center pt-8">No State Data Available for {selectedCountry}</p>
//               )
//             ) : (
//               <p className="text-center pt-8">No Data Available</p>
//             )}
//           </div>
//         </div>
//       </div>
//     </div> 
//   );
// };

// export default ChartComponent;










// new code ..................................
// import React, { useEffect, useState } from 'react';
// import { Doughnut } from 'react-chartjs-2';
// import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
// import { getChartData } from '../../FeatureRedux/chartReducer/donatchartget';
// import { useDispatch, useSelector } from 'react-redux';

// // Register Chart.js elements
// ChartJS.register(ArcElement, Tooltip, Legend, Title);

// const ChartComponent = () => {
//   const dispatch = useDispatch();
//   const [selectedCountry, setSelectedCountry] = useState('India'); // Set India as default
//   const [activeChart, setActiveChart] = useState('states'); // Default to states since India is selected

//   // Redux state
//   const { data, loading, error, isSuccess } = useSelector((state) => state.getChartData);

//   useEffect(() => {
//     dispatch(getChartData());
//   }, [dispatch]);

//   // Color palettes
//   const sectorColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'];
//   const monetaryColors = ['#FFCE56', '#36A2EB', '#FF6384', '#4BC0C0', '#9966FF'];
//   const countryColors = ['#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0'];
//   const stateColors = [
//     '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
//     '#FF9F40', '#8AC24A', '#00BCD4', '#E91E63', '#9C27B0',
//     '#3DDC97', '#F7B32B', '#D64550', '#5C2751', '#6457A6',
//     '#9BC53D', '#E55934', '#FA7921', '#3E5C76', '#1D1E2C'
//   ];

//   // Safely get data with fallbacks
//   const sectors = data?.data?.sectors || {};
//   const monetaryRange = data?.data?.MonetaryRange || {};
//   const countriesData = data?.data?.countryData || {};

//   // Prepare chart data for each graph
//   const sectorsData = {
//     labels: Object.keys(sectors),
//     datasets: [{
//       data: Object.values(sectors),
//       backgroundColor: sectorColors,
//       hoverBackgroundColor: sectorColors,
//     }],
//   };

//   const moneyData = {
//     labels: Object.keys(monetaryRange),
//     datasets: [{
//       data: Object.values(monetaryRange),
//       backgroundColor: monetaryColors,
//       hoverBackgroundColor: monetaryColors,
//     }],
//   };

//   const countriesChartData = {
//     labels: Object.keys(countriesData),
//     datasets: [{
//       data: Object.values(countriesData).map(item => item.count),
//       backgroundColor: countryColors,
//       hoverBackgroundColor: countryColors,
//     }],
//   };

//   const getStatesChartData = () => {
//     if (!selectedCountry || !countriesData[selectedCountry]?.states) return null;

//     const states = countriesData[selectedCountry].states;

//     // Handle both array and object formats
//     const statesLabels = Array.isArray(states) ? states : Object.keys(states);
//     const statesValues = Array.isArray(states) 
//       ? new Array(states.length).fill(1) // Equal values if array
//       : Object.values(states); // Actual values if object

//     // Assign a unique color to each state
//     const colorsForStates = statesLabels.map((_, index) => 
//       stateColors[index % stateColors.length]
//     );

//     return {
//       labels: statesLabels,
//       datasets: [{
//         data: statesValues,
//         backgroundColor: colorsForStates,
//         hoverBackgroundColor: colorsForStates,
//       }],
//     };
//   };

//   const handleCountrySelected = (e) => {
//     const country = e.target.value;
//     setSelectedCountry(country);
//     setActiveChart(country ? 'states' : 'countries');
//   };

//   const chartOptions = {
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: { 
//         position: 'bottom',
//         labels: {
//           padding: 20,
//           boxWidth: 12
//         }
//       },
//       title: { 
//         display: true,
//         padding: { top: 10, bottom: 10 },
//         font: { size: 16 }
//       },
//     },
//   };

//   return (
//     <div className="p-4 bg-gray-900"
//     style={{ 
//       overflowY: 'scroll',
//       height: '100vh',
//       width: '100%',
//       scrollbarWidth: '1px',
//       msOverflowStyle: 'none'
//     }}>
//       {/* Sector and Monetary Charts - Always Visible */}
//       <div className="flex flex-wrap gap-8 justify-center mb-8">
//         {/* Sectors Chart */}
//         <div className="w-96 h-80 bg-gray-900 text-white  border shadow p-4 rounded-lg" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="0">
//           <h2 className="text-center mb-2 text-lg font-semibold">Sectors Data</h2>
//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(sectors).length > 0 ? (
//               <Doughnut 
//                 data={sectorsData} 
//                 options={{
//                   ...chartOptions,
//                   plugins: {
//                     ...chartOptions.plugins,
//                     title: { ...chartOptions.plugins.title, text: 'Sectors Distribution', color: 'white' }
//                   }
//                 }} 
//               />
//             ) : (
//               <p className="text-center pt-8 text-light">No Sector Data Available</p>
//             )}
//           </div>
//         </div>

//         {/* Monetary Ranges Chart */}
//         <div className="w-96 h-80 bg-gray-900 text-white border shadow p-4 rounded-lg" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="1000">
//           <h2 className="text-center mb-2 text-lg font-semibold">Monetary Ranges</h2>
//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess && Object.keys(monetaryRange).length > 0 ? (
//               <Doughnut 
//                 data={moneyData} 
//                 options={{
//                   ...chartOptions,
//                   plugins: {
//                     ...chartOptions.plugins,
//                     title: { ...chartOptions.plugins.title, text: 'Monetary Ranges', color: 'white' }
//                   }
//                 }} 
//               />
//             ) : (
//               <p className="text-center pt-8 text-white">No Monetary Data Available</p>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Combined Country/State Chart */}
//       <div className="flex justify-center">
//         <div className="w-96 h-80 bg-gray-900 text-white border shadow p-6 rounded-lg" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="2000">
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-center">Choose Country</label>
//             <select
//               className="mt-2 block w-full px-3 py-2 border bg-gray-900 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//               onChange={handleCountrySelected}
//               value={selectedCountry}
//             >
//               <option value="">Show All Countries</option>
//               {isSuccess && Object.keys(countriesData).map((country, index) => (
//                 <option key={index} value={country}>{country}</option>
//               ))}
//             </select>
//           </div>

//           <div className="h-60">
//             {loading ? (
//               <p className="text-center pt-8">Loading...</p>
//             ) : error ? (
//               <p className="text-center pt-8 text-red-500">Error: {error}</p>
//             ) : isSuccess ? (
//               activeChart === 'countries' ? (
//                 Object.keys(countriesData).length > 0 ? (
//                   <Doughnut 
//                     data={countriesChartData} 
//                     options={{
//                       ...chartOptions,
//                       plugins: {
//                         ...chartOptions.plugins,
//                         title: { ...chartOptions.plugins.title, text: 'Countries Distribution', color: 'white' }
//                       }
//                     }} 
//                   />
//                 ) : (
//                   <p className="text-center pt-8 text-white">No Country Data Available</p>
//                 )
//               ) : getStatesChartData() ? (
//                 <Doughnut 
//                   data={getStatesChartData()} 
//                   options={{
//                     ...chartOptions,
//                     plugins: {
//                       ...chartOptions.plugins,
//                       title: { 
//                         ...chartOptions.plugins.title, 
//                         text: `${selectedCountry} States`,
//                         color: 'white'
//                       }
//                     }
//                   }} 
//                 />
//               ) : (
//                 <p className="text-center pt-8 text-white">No State Data Available for {selectedCountry}</p>
//               )
//             ) : (
//               <p className="text-center pt-8 text-white">No Data Available</p>
//             )}
//           </div>
//         </div>
//       </div>
//     </div> 
//   );
// };

// export default ChartComponent;
